import React, { useEffect, useRef, useState } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { RefreshCw, CameraOff, Camera } from "lucide-react";

/**
 * Polled-image webcam display.
 *
 * For cameras where a proper HLS stream isn't viable (flaky source,
 * extreme bandwidth constraints), this component polls a static JPEG
 * snapshot URL on a fixed interval (default 60 s) and shows the most
 * recent frame. The backend refreshes the underlying file on the same
 * schedule, so polling every 60 s is enough to always show a fresh
 * image without wasted requests.
 *
 * The container uses CSS `aspect-ratio` to match the camera's native
 * frame so there's no letterbox or pillarbox.
 */
const REFRESH_INTERVAL_MS = 60000;
const AGO_TICK_MS = 10000;

const WebcamSnapshot = ({
  snapshotUrl,
  refreshKey,
  onRefresh,
  aspectRatio = "16 / 9",
  className = "",
}) => {
  const [src, setSrc] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(false);
  const [, forceTick] = useState(0); // forces "X ago" label re-render
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL || ""}${snapshotUrl}`;

    const fetchSnapshot = () => {
      if (cancelledRef.current) return;
      const cacheBusted = `${fullUrl}?t=${Date.now()}`;
      const img = new Image();
      img.onload = () => {
        if (cancelledRef.current) return;
        setSrc(cacheBusted);
        setLastUpdate(Date.now());
        setError(false);
      };
      img.onerror = () => {
        if (cancelledRef.current) return;
        setError(true);
      };
      img.src = cacheBusted;
    };

    fetchSnapshot();
    const fetchId = setInterval(fetchSnapshot, REFRESH_INTERVAL_MS);
    const tickId = setInterval(() => forceTick((t) => t + 1), AGO_TICK_MS);

    return () => {
      cancelledRef.current = true;
      clearInterval(fetchId);
      clearInterval(tickId);
    };
  }, [snapshotUrl, refreshKey]);

  const agoLabel = (() => {
    if (!lastUpdate) return "Loading…";
    const seconds = Math.floor((Date.now() - lastUpdate) / 1000);
    if (seconds < 5) return "Just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  })();

  const showLoading = !src && !error;
  const showError = error && !src;

  return (
    <div className={`relative bg-black overflow-hidden ${className}`}>
      <div className="w-full" style={{ aspectRatio }}>
        {src && (
          <img
            src={src}
            alt="Webcam snapshot"
            className="w-full h-full object-contain bg-black"
          />
        )}
        {showLoading && (
          <div className="w-full h-full flex items-center justify-center bg-black">
            <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {showError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85 text-center p-6 pointer-events-none">
          <CameraOff className="w-12 h-12 text-gray-400 mb-3" />
          <p className="text-white font-semibold text-lg">Snapshot unavailable</p>
          <p className="text-gray-300 text-sm mt-1">
            Try the refresh button.
          </p>
        </div>
      )}

      <div className="absolute top-4 left-4 pointer-events-none">
        <Badge className="bg-blue-600 hover:bg-blue-700 flex items-center gap-1.5">
          <Camera className="w-3 h-3" />
          {agoLabel}
        </Badge>
      </div>
      <div className="absolute top-4 right-4">
        <Button
          size="sm"
          variant="secondary"
          className="bg-black/50 hover:bg-black/70 text-white border-0"
          onClick={onRefresh}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default WebcamSnapshot;
